import { CountryCode } from '@rover/constants/src/geography/geo.constants';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import { getDefaultLanguageByCountryCode } from '@rover/utilities/roverGeo.constants';

/**
 * Whether the homepage is being viewed in the country's default locale
 * (e.g. en-us in the US, en-ca in Canada) as opposed to a secondary locale
 * (e.g. fr-ca in Canada).
 *
 * Computed entirely on the frontend by comparing the active request language to
 * the country's default language from `roverGeo.constants`, which is generated
 * from each country's backend `default_language` and kept in sync automatically
 * — so this stays correct for every active Rover country without a hard-coded
 * map or an extra backend field.
 *
 * Defaults to `false` when the configuration has not loaded yet: callers use
 * this to gate into the geo-browse entrypoint, which is only translated for the
 * default locale, so "unknown" should fall back to the safe (non-entrypoint)
 * behavior rather than risk an untranslated experience.
 */
const useIsDefaultLocale = (): boolean => {
  const { settings } = useFrontendConfigurationShim();
  const countryCode = settings?.countryCode;
  const locale = settings?.language;
  if (!countryCode || !locale) return false;
  return locale === getDefaultLanguageByCountryCode(countryCode as CountryCode);
};

export default useIsDefaultLocale;
